import React, { useState, useEffect, useCallback } from 'react';
import { X } from '../icons';

/**
 * Reusable modal component replacing window.confirm, window.prompt, and window.alert
 */

// ── ConfirmModal ──────────────────────────────────────────
export function ConfirmModal({ isOpen, title, message, confirmText = 'Confirm', cancelText = 'Cancel', danger = false, onConfirm, onCancel }) {
  useEffect(() => {
    if (isOpen) {
      const handler = (e) => { if (e.key === 'Escape') onCancel(); };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
  }, [isOpen, onCancel]);

  if (!isOpen) return null;
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onCancel}><X size={18} /></button>
        {title && <h3 className="modal-title">{title}</h3>}
        <p className="modal-message">{message}</p>
        <div className="modal-actions">
          <button className="btn btn-outline modal-btn" onClick={onCancel}>{cancelText}</button>
          <button className={`btn modal-btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>{confirmText}</button>
        </div>
      </div>
    </div>
  );
}

// ── PromptModal ──────────────────────────────────────────
export function PromptModal({ isOpen, title, message, placeholder = '', defaultValue = '', confirmText = 'OK', cancelText = 'Cancel', onConfirm, onCancel }) {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    if (isOpen) setValue(defaultValue);
  }, [isOpen, defaultValue]);

  useEffect(() => {
    if (isOpen) {
      const handler = (e) => { if (e.key === 'Escape') onCancel(); };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
  }, [isOpen, onCancel]);

  if (!isOpen) return null;
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onCancel}><X size={18} /></button>
        {title && <h3 className="modal-title">{title}</h3>}
        {message && <p className="modal-message">{message}</p>}
        <input
          className="modal-input"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') onConfirm(value); }}
        />
        <div className="modal-actions">
          <button className="btn btn-outline modal-btn" onClick={onCancel}>{cancelText}</button>
          <button className="btn btn-primary modal-btn" onClick={() => onConfirm(value)}>{confirmText}</button>
        </div>
      </div>
    </div>
  );
}

// ── AlertModal ──────────────────────────────────────────
export function AlertModal({ isOpen, title, message, buttonText = 'OK', onClose }) {
  useEffect(() => {
    if (isOpen) {
      const handler = (e) => { if (e.key === 'Escape' || e.key === 'Enter') onClose(); };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}><X size={18} /></button>
        {title && <h3 className="modal-title">{title}</h3>}
        <p className="modal-message">{message}</p>
        <div className="modal-actions" style={{ justifyContent: 'center' }}>
          <button className="btn btn-primary modal-btn" onClick={onClose}>{buttonText}</button>
        </div>
      </div>
    </div>
  );
}

// ── useConfirm hook ──────────────────────────────────────
export function useConfirm() {
  const [state, setState] = useState({ isOpen: false, title: '', message: '', danger: false, confirmText: 'Confirm', resolve: null });

  const confirm = useCallback(({ title = 'Confirm', message, danger = false, confirmText = 'Confirm' } = {}) => {
    return new Promise((resolve) => {
      setState({ isOpen: true, title, message, danger, confirmText, resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    state.resolve?.(true);
    setState(s => ({ ...s, isOpen: false }));
  }, [state]);

  const handleCancel = useCallback(() => {
    state.resolve?.(false);
    setState(s => ({ ...s, isOpen: false }));
  }, [state]);

  const ConfirmDialog = (
    <ConfirmModal
      isOpen={state.isOpen}
      title={state.title}
      message={state.message}
      danger={state.danger}
      confirmText={state.confirmText}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return [confirm, ConfirmDialog];
}

// ── usePrompt hook ──────────────────────────────────────
export function usePrompt() {
  const [state, setState] = useState({ isOpen: false, title: '', message: '', placeholder: '', defaultValue: '', resolve: null });

  const prompt = useCallback(({ title = '', message = '', placeholder = '', defaultValue = '' } = {}) => {
    return new Promise((resolve) => {
      setState({ isOpen: true, title, message, placeholder, defaultValue, resolve });
    });
  }, []);

  const handleConfirm = useCallback((value) => {
    state.resolve?.(value);
    setState(s => ({ ...s, isOpen: false }));
  }, [state]);

  const handleCancel = useCallback(() => {
    state.resolve?.(null);
    setState(s => ({ ...s, isOpen: false }));
  }, [state]);

  const PromptDialog = (
    <PromptModal
      isOpen={state.isOpen}
      title={state.title}
      message={state.message}
      placeholder={state.placeholder}
      defaultValue={state.defaultValue}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return [prompt, PromptDialog];
}
